/* HR — Calendar (R7 fully wired)
 *
 *  Endpoints:
 *    GET    /api/calendar/events?from_date=&to_date=&status=&page=&page_size=
 *    GET    /api/calendar/events/:id
 *    POST   /api/calendar/events                      (任何登入者；team 共享)
 *    PATCH  /api/calendar/events/:id                  (Google-synced 事件不可改 → 400)
 *    DELETE /api/calendar/events/:id                  (Google-synced 事件不可刪 → 400)
 *
 *  Note: 後端 schema 沒有「type」分類欄；本頁以 status 區分 colour
 *  (confirmed = accent, tentative = warn, cancelled = down)。Google 同步事件
 *  以「G」徽章標示，且按鈕停用。
 */

/* Render-time function, not a module-level const: HR colours are var(--gx-*) under Expressive
 * and restore() must bring Bento back (SPEC §1.2「內頁換膚」, tests/test_expressive_pages.cjs §7). */
function calStatusMeta() {
  return {
    confirmed: { label: '已確認', color: HR.accent },
    tentative: { label: '暫定',   color: HR.warn },
    cancelled: { label: '已取消', color: HR.down },
    '':        { label: '—',     color: HR.accent },
  };
}

/* Expressive only: status → tonal tint (GxT.tint). Hue family matches calStatusMeta()
 * (accent / warn / down) so the colour meaning is unchanged; only the rendering is tonal. */
function calGxTone(status) {
  const kind = { confirmed: 'ok', tentative: 'warn', cancelled: 'down', holiday: 'down' }[status || 'confirmed'] || 'ok';
  return GxCore.tone(kind);
}

function eventDateBounds(ev) {
  // Adapt API response to CalendarMonth shape (from_date / to_date / color)
  const start = (ev.start_at || '').slice(0, 10);
  const end   = (ev.end_at   || ev.start_at || '').slice(0, 10);
  const color = calStatusMeta()[ev.status || '']?.color || HR.accent;
  // GX adds {bg, fg} tints consumed by CalendarMonth / the day list; Bento shape is unchanged.
  return gxUiOn()
    ? { from_date: start, to_date: end, color, bg: calGxTone(ev.status).bg, fg: calGxTone(ev.status).fg }
    : { from_date: start, to_date: end, color };
}

/* Narrow screens (≤767px) stack the selected-day list below the month grid and the
 * grid only shows dots, so after a tap bring that list into view. No-op on wider
 * screens or without matchMedia / DOM (node tests). */
function calRevealDayPanel() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  if (!window.matchMedia('(max-width: 767px)').matches) return;
  setTimeout(() => {
    const el = document.querySelector('.hr-cal-content-grid > :last-child');
    if (!el || !el.getBoundingClientRect || !el.scrollIntoView) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    if (r.top > vh * 0.6) el.scrollIntoView({ behavior: 'smooth', block: r.height > vh * 0.5 ? 'start' : 'center' });
  }, 0);
}

function EventDetailModal({ event, onEdit, onDelete, onClose }) {
  const statusMeta = calStatusMeta();
  const meta = statusMeta[event.status || ''] || statusMeta[''];
  const bounds = eventDateBounds(event);
  const locked = !!event.google_event_id;
  return (
    <Modal
      open onClose={onClose}
      title={event.title || '（無標題）'}
      subtitle="事件詳情"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>關閉</Button>
          {!locked && (
            <>
              <Button variant="danger" onClick={onDelete}>刪除</Button>
              <Button variant="primary" onClick={onEdit}>編輯</Button>
            </>
          )}
        </>
      }
    >
      <div style={uiStyle({
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '4px 10px', borderRadius: 4, marginBottom: 16,
        background: GxCore.alpha(bounds.color, '20'), color: bounds.color,
        fontSize: FontSizes.trading_lg, fontWeight: 600, letterSpacing: '0.06em',
      }, {
        padding: '4px 10px', borderRadius: GxT.radius.chip,
        background: bounds.bg, color: bounds.fg, fontSize: 12, fontWeight: 700, letterSpacing: 0,
      })}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: bounds.color }} />
        {meta.label}
        {locked && (
          <span style={uiStyle({
            marginLeft: 4, padding: '1px 5px', borderRadius: 2,
            background: '#553C7B', color: '#fff', fontSize: FontSizes.trading_xxs, fontWeight: 700,
            fontFamily: HR.mono,
          }, { background: GxT.color.done, borderRadius: GxT.radius.chipSm, padding: '1px 6px' })}>G</span>
        )}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: '90px 1fr', gap: 12,
        fontSize: FontSizes.xs, color: HR.ink,
      }}>
        <span style={{ color: HR.inkFaint }}>期間</span>
        <span style={{ fontFamily: HR.mono }}>
          {event.all_day
            ? `${bounds.from_date}${bounds.from_date !== bounds.to_date ? ` → ${bounds.to_date}` : ''} (全日)`
            : `${fmtDateTime(event.start_at)} → ${fmtDateTime(event.end_at)}`}
        </span>
        {event.location && (
          <>
            <span style={{ color: HR.inkFaint }}>地點</span>
            <span style={{ fontFamily: HR.serif, fontSize: FontSizes.sm }}>{event.location}</span>
          </>
        )}
        {event.description && (
          <>
            <span style={{ color: HR.inkFaint }}>說明</span>
            <span style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, lineHeight: 1.5 }}>{event.description}</span>
          </>
        )}
        {event.attendees_json && (
          <>
            <span style={{ color: HR.inkFaint }}>參與者</span>
            <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{event.attendees_json}</span>
          </>
        )}
        {locked && (
          <>
            <span style={{ color: HR.inkFaint }}>來源</span>
            <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
              Google Calendar 同步 · 不可手動編輯／刪除
            </span>
          </>
        )}
      </div>
    </Modal>
  );
}

function EventForm({ initial, defaultDate, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!initial?.id;
  const [title, setTitle] = React.useState(initial?.title || '');
  const [allDay, setAllDay] = React.useState(initial?.all_day ?? true);
  const [from, setFrom] = React.useState(
    initial?.start_at?.slice(0, 10) || defaultDate || todayStr()
  );
  const [to, setTo] = React.useState(
    initial?.end_at?.slice(0, 10) || defaultDate || todayStr()
  );
  const [startTime, setStartTime] = React.useState(
    initial?.start_at?.slice(11, 16) || '09:00'
  );
  const [endTime, setEndTime] = React.useState(
    initial?.end_at?.slice(11, 16) || '10:00'
  );
  const [location, setLocation] = React.useState(initial?.location || '');
  const [description, setDescription] = React.useState(initial?.description || '');
  const [status, setStatus] = React.useState(initial?.status || 'confirmed');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!title.trim()) { toast('標題必填', 'error'); return; }
    if (from > to) { toast('結束日不可早於開始日', 'error'); return; }
    setSubmitting(true);
    try {
      const start_at = allDay ? `${from}T00:00:00Z` : `${from}T${startTime}:00Z`;
      const end_at   = allDay ? `${to}T23:59:59Z`   : `${to}T${endTime}:00Z`;
      const body = {
        title: title.trim(),
        all_day: allDay,
        start_at, end_at,
        location: location.trim() || null,
        description: description.trim() || null,
        status,
      };
      if (isEdit) await api('PATCH', `/calendar/events/${initial.id}`, body);
      else        await api('POST',  '/calendar/events', body);
      toast(isEdit ? '已更新事件' : '已建立事件', 'ok');
      onSaved();
    } catch (e) {
      toast((isEdit ? '更新' : '建立') + '失敗：' + (e.detail || ''), 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title={isEdit ? `編輯事件 — ${initial.title || ''}` : '新增事件'}
      subtitle="Calendar Event"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '處理中…' : (isEdit ? '儲存' : '建立')}</Button>
        </>
      }
    >
      <div className="hr-cal-event-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="標題" required span={2}>
          <TextInput value={title} onChange={setTitle} placeholder="e.g. 月度業績檢討" autoFocus />
        </Field>
        <Field label="全日活動" span={2}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: FontSizes.xs, cursor: 'pointer' }}>
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} style={{ width: 16, height: 16, accentColor: HR.accent }} />
            <span style={{ color: HR.inkSoft }}>是 — 跨日 / 整日事件</span>
          </label>
        </Field>
        <Field label="開始" required>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="結束" required>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle()} />
        </Field>
        {!allDay && (
          <>
            <Field label="開始時間">
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inputStyle()} />
            </Field>
            <Field label="結束時間">
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inputStyle()} />
            </Field>
          </>
        )}
        <Field label="地點" span={2}>
          <TextInput value={location} onChange={setLocation} placeholder="e.g. 大會議室 / Zoom" />
        </Field>
        <Field label="說明" span={2}>
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <Field label="狀態" span={2}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle()}>
            <option value="confirmed">已確認</option>
            <option value="tentative">暫定</option>
            <option value="cancelled">已取消</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function CalendarPage() {
  const toast = useToast();
  const today = new Date();
  const [year, setYear] = React.useState(today.getFullYear());
  const [month, setMonth] = React.useState(today.getMonth() + 1);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [editing, setEditing] = React.useState(null);  // null | {} (new) | event (edit)
  const [view, setView] = React.useState('month');
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [holidayCfg, setHolidayCfg] = React.useState({});  // { '<year>': ['YYYY-MM-DD', ...] }

  /* Fetch events for current month (with a few days buffer for week spillover). */
  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      // Include the full visible grid range: 1st of month - 7 days to end of month + 7
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      const fromBuf = new Date(firstDay); fromBuf.setDate(fromBuf.getDate() - 7);
      const toBuf   = new Date(lastDay);  toBuf.setDate(toBuf.getDate() + 7);
      const fromStr = fromBuf.toISOString().slice(0, 10);
      const toStr   = toBuf.toISOString().slice(0, 10);
      const resp = await api('GET', `/calendar/events?from_date=${fromStr}&to_date=${toStr}&page_size=200`).catch(() => ({ items: [] }));
      setEvents(resp.items || []);
    } finally { setLoading(false); }
  }, [year, month]);

  React.useEffect(() => { refresh(); }, [refresh]);

  /* National holidays (單一真相來源：payroll_config.json) — fetch once, derive
   * synthetic all-day pseudo-events. These are reminders only: NON-EDITABLE. */
  React.useEffect(() => {
    api('GET', '/settings/payroll-config')
      .then(cfg => setHolidayCfg((cfg && cfg.holidays) || {}))
      .catch(() => setHolidayCfg({}));
  }, []);

  /* Synthetic holiday pseudo-events for the visible month/year.
   * No numeric `id` + is_holiday flag → render but cannot edit/delete/PATCH. */
  const holidayEvents = React.useMemo(() => {
    const dates = holidayCfg[String(year)] || [];
    return dates.map(d => ({
      is_holiday: true,
      title: '國定假日',
      all_day: true,
      start_at: d,
      end_at: d,
      from_date: d,
      to_date: d,
      color: HR.down,  // 紅色強調 → 明確是「提醒」而非團隊事件
      ...(gxUiOn() ? { bg: calGxTone('holiday').bg, fg: calGxTone('holiday').fg } : {}),
    }));
  }, [holidayCfg, year]);

  /* Adapted shape for CalendarMonth (real events + synthetic holidays). */
  const adapted = React.useMemo(
    () => [
      ...events.map(ev => ({ ...ev, ...eventDateBounds(ev) })),
      ...holidayEvents,
    ],
    [events, holidayEvents]
  );

  const handleDateClick = (date) => {
    setSelectedDate(date);
    calRevealDayPanel();
  };

  /* 國定假日是合成提醒（無 id）— 不可編輯/刪除，點擊不開啟詳情/編輯表單。 */
  const openEvent = (ev) => { if (ev && ev.is_holiday) return; setSelectedEvent(ev); };

  const dayEvents = selectedDate
    ? adapted.filter(e => selectedDate >= e.from_date && selectedDate <= e.to_date)
    : [];

  /* Status stats */
  const statusStats = React.useMemo(() => {
    const m = {};
    events.forEach(e => {
      const k = e.status || 'confirmed';
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [events]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api('DELETE', `/calendar/events/${confirmDelete.id}`);
      toast('已刪除', 'ok');
      setConfirmDelete(null);
      setSelectedEvent(null);
      refresh();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
    finally { setDeleting(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="系統 · Calendar"
        title="行事曆"
        italic="Company Calendar"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <ViewSwitcher
              views={[
                { key: 'month', label: '月',   icon: 'calendar' },
                { key: 'list',  label: '列表', icon: 'file-text' },
              ]}
              value={view}
              onChange={setView}
            />
            <Button variant="primary" icon="plus" onClick={() => setEditing({})}>新增事件</Button>
          </div>
        }
      />

      {/* Legend / stats */}
      <div style={uiStyle({
        display: 'flex', gap: 14, flexWrap: 'wrap',
        padding: '12px 16px', marginBottom: 16,
        background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
        fontSize: FontSizes.trading_xl,
      }, {
        alignItems: 'center', gap: 16, padding: '12px 20px',
        border: 'none', borderRadius: GxT.radius.card, fontSize: 13,
      })}>
        {['confirmed', 'tentative', 'cancelled'].map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: HR.inkSoft, fontWeight: uiPick(undefined, 600) }}>
            <span style={{ width: 9, height: 9, borderRadius: uiPick(2, '50%'), background: calStatusMeta()[k].color }} />
            {calStatusMeta()[k].label}
            <span style={uiStyle({ fontFamily: HR.mono, color: HR.inkFaint }, {
              fontFamily: GxT.font, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
              color: calGxTone(k).fg, background: calGxTone(k).bg, borderRadius: GxT.radius.chipSm, padding: '1px 7px',
            })}>{statusStats[k] || 0}</span>
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <span style={{ color: HR.inkFaint, fontFamily: HR.mono, fontSize: FontSizes.trading_lg }}>
          {events.length} 筆事件 · {events.filter(e => e.google_event_id).length} 來自 Google 同步
        </span>
      </div>

      {loading ? (
        <SkeletonTable rows={6} />
      ) : view === 'month' ? (
        <div className="hr-cal-content-grid" style={{ display: 'grid', gridTemplateColumns: selectedDate ? '1fr 320px' : '1fr', gap: 18 }}>
          <Card padding="0">
            <CalendarMonth
              year={year}
              month={month}
              events={adapted}
              onDateClick={handleDateClick}
              onEventClick={openEvent}
              selectedDate={selectedDate}
              onMonthChange={(y, m) => { setYear(y); setMonth(m); setSelectedDate(null); }}
            />
          </Card>

          {selectedDate && (
            <Card
              eyebrow={selectedDate}
              title={`${dayEvents.length} 個事件`}
              italic="Selected Day"
              right={
                <button onClick={() => setSelectedDate(null)} aria-label={uiPick(undefined, '關閉')} style={uiStyle({
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: HR.inkFaint, fontSize: FontSizes.xs, padding: 4,
                }, {
                  width: 36, height: 36, padding: 0, borderRadius: GxT.radius.btnSm,
                  background: GxT.color.s2, color: GxT.color.onV, fontSize: 18, lineHeight: 1,
                })}>×</button>
              }
            >
              {dayEvents.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs }}>
                  此日無事件
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dayEvents.map((ev, di) => (
                    <div
                      key={ev.id ?? `hol-${ev.from_date}-${di}`}
                      onClick={() => openEvent(ev)}
                      style={uiStyle({
                        padding: 10, borderRadius: 5,
                        cursor: ev.is_holiday ? 'default' : 'pointer',
                        borderLeft: `3px solid ${ev.color}`,
                        background: HR.surfaceAlt,
                        transition: 'background 120ms',
                      }, { padding: '10px 12px', borderRadius: GxT.radius.inner, borderLeft: 'none' })}
                      onMouseEnter={(e) => { if (!ev.is_holiday) e.currentTarget.style.background = GxCore.alpha(ev.color, '15'); }}
                      onMouseLeave={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                    >
                      <div style={uiStyle({
                        fontSize: FontSizes.trading_sm, color: ev.color, letterSpacing: '0.08em',
                        textTransform: 'uppercase', fontWeight: 600, marginBottom: 4,
                      }, {
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 12, letterSpacing: 0, textTransform: 'none', fontWeight: 700,
                        color: ev.fg || ev.color, background: ev.bg, borderRadius: GxT.radius.chipSm, padding: '2px 8px',
                      })}>
                        {ev.is_holiday ? '國定假日' : calStatusMeta()[ev.status || 'confirmed']?.label}
                        {ev.google_event_id && (
                          <span style={uiStyle({ marginLeft: 6, padding: '1px 4px', background: '#553C7B', color: '#fff', borderRadius: 2, fontSize: FontSizes.trading_3xs }, { marginLeft: 0, padding: '0 5px', background: GxT.color.done, borderRadius: 4, fontSize: 10.5 })}>G</span>
                        )}
                      </div>
                      <div style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: uiPick(500, 700), color: HR.ink }}>
                        {ev.title || '（無標題）'}
                      </div>
                      {ev.location && (
                        <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 2 }}>{ev.location}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Button
                variant="secondary" size="sm" icon="plus"
                onClick={() => setEditing({ _defaultDate: selectedDate })}
                style={{ marginTop: 14, width: '100%', justifyContent: 'center' }}
              >
                在此日新增事件
              </Button>
            </Card>
          )}
        </div>
      ) : (
        <Card eyebrow="List" title={`${year} 年 ${month} 月所有事件`} italic={`${events.length} 筆`} padding="0">
          {events.length === 0 ? (
            <EmptyState icon="calendar" title="本月無事件" hint="點右上角「新增事件」開始。" />
          ) : (
            <div className="hr-cal-list-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={uiStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }, { letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontSize: 12 })}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>期間</th>
                  <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>狀態</th>
                  <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>標題</th>
                  <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>地點</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'left', fontWeight: 500 }}>說明</th>
                </tr>
              </thead>
              <tbody>
                {[...adapted].sort((a, b) => a.from_date.localeCompare(b.from_date)).map((ev, ri) => (
                  <tr key={ev.id ?? `hol-${ev.from_date}-${ri}`} onClick={() => openEvent(ev)} style={{ borderTop: `1px solid ${HR.ruleSoft}`, cursor: ev.is_holiday ? 'default' : 'pointer' }}>
                    <td style={{ padding: '12px 16px 12px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                      {ev.from_date}{ev.from_date !== ev.to_date && ` → ${ev.to_date}`}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={uiStyle({ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: FontSizes.trading_xl, color: ev.color, fontWeight: 500 }, {
                        fontSize: 12, fontWeight: 700, color: ev.fg || ev.color, background: ev.bg,
                        borderRadius: GxT.radius.chipSm, padding: '2px 8px', whiteSpace: 'nowrap',
                      })}>
                        <span style={{ width: 6, height: 6, borderRadius: uiPick(1, '50%'), background: ev.color }} />
                        {ev.is_holiday ? '國定假日' : calStatusMeta()[ev.status || 'confirmed']?.label}
                        {ev.google_event_id && <span style={uiStyle({ marginLeft: 4, padding: '1px 4px', background: '#553C7B', color: '#fff', borderRadius: 2, fontSize: FontSizes.trading_xxs, fontFamily: HR.mono }, { padding: '0 5px', background: GxT.color.done, borderRadius: 4 })}>G</span>}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: HR.serif, fontWeight: uiPick(500, 700) }}>{ev.title || '（無標題）'}</td>
                    <td style={{ padding: '12px 16px', fontSize: FontSizes.xxs, color: HR.inkSoft }}>{ev.location || '—'}</td>
                    <td style={{ padding: '12px 24px 12px 16px', color: HR.inkSoft, fontSize: FontSizes.xxs }}>{ev.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEdit={() => { setEditing(selectedEvent); setSelectedEvent(null); }}
          onDelete={() => { setConfirmDelete(selectedEvent); setSelectedEvent(null); }}
        />
      )}
      {editing !== null && (
        <EventForm
          initial={editing.id ? editing : null}
          defaultDate={editing._defaultDate}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="刪除事件"
        message={confirmDelete ? `確定刪除「${confirmDelete.title || '（無標題）'}」？此操作無法復原。` : ''}
        danger busy={deleting}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </>
  );
}

window.CalendarPage = CalendarPage;
